import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { Package, Plus, Edit, Search } from 'lucide-react';

export default function ItemMasterPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemDialog, setItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemForm, setItemForm] = useState({ 
    name: '', description: '', sku: '', unit: 'nos', category: '',
    min_stock_level: 0, max_stock_level: 0, reorder_point: 0, reorder_quantity: 0, 
    standard_cost: 0, tax_rate: 18 
  });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/stock-items');
      setItems(res.data);
    } catch (err) {
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSaveItem = async () => {
    try {
      if (selectedItem) {
        await api.put(`/stock-items/${selectedItem.id}`, itemForm);
        toast.success('Item updated');
      } else {
        await api.post('/stock-items', itemForm);
        toast.success('Item created');
      }
      setItemDialog(false);
      resetForm();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save item');
    }
  };

  const resetForm = () => {
    setSelectedItem(null);
    setItemForm({ 
      name: '', description: '', sku: '', unit: 'nos', category: '',
      min_stock_level: 0, max_stock_level: 0, reorder_point: 0, reorder_quantity: 0, 
      standard_cost: 0, tax_rate: 18 
    });
  };

  const openEditDialog = (item) => {
    setSelectedItem(item);
    setItemForm({
      name: item.name,
      description: item.description || '',
      sku: item.sku || '',
      unit: item.unit || 'nos',
      category: item.category || '',
      min_stock_level: item.min_stock_level || 0,
      max_stock_level: item.max_stock_level || 0,
      reorder_point: item.reorder_point || 0,
      reorder_quantity: item.reorder_quantity || 0,
      standard_cost: item.standard_cost || 0,
      tax_rate: item.tax_rate || 18
    });
    setItemDialog(true);
  };

  const filteredItems = items.filter(item => 
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="item-master-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Item Master</h1>
          <p className="text-gray-500">Manage inventory items and SKUs</p>
        </div>
        <Button onClick={() => { resetForm(); setItemDialog(true); }} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="h-4 w-4 mr-2" />Add Item
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                placeholder="Search items..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Reorder Point</TableHead>
                <TableHead>Std Cost</TableHead>
                <TableHead>Tax %</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map(item => (
                <TableRow key={item.id}>
                  <TableCell className="font-mono text-sm">{item.sku || '-'}</TableCell>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>{item.category || '-'}</TableCell>
                  <TableCell><Badge variant="outline">{item.unit}</Badge></TableCell>
                  <TableCell>{item.reorder_point}</TableCell>
                  <TableCell className="font-mono">₹{item.standard_cost?.toFixed(2)}</TableCell>
                  <TableCell>{item.tax_rate}%</TableCell>
                  <TableCell>
                    <Badge className={item.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(item)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredItems.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p>{searchTerm ? 'No items match your search.' : 'No items found. Add your first item to get started.'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Item Dialog */}
      <Dialog open={itemDialog} onOpenChange={setItemDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Item Name *</Label>
                <Input 
                  value={itemForm.name} 
                  onChange={(e) => setItemForm({...itemForm, name: e.target.value})}
                  placeholder="Rice (Basmati)"
                />
              </div>
              <div className="grid gap-2">
                <Label>SKU</Label>
                <Input 
                  value={itemForm.sku}
                  onChange={(e) => setItemForm({...itemForm, sku: e.target.value})}
                  placeholder="RICE-BAS-001"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea 
                value={itemForm.description}
                onChange={(e) => setItemForm({...itemForm, description: e.target.value})}
                placeholder="Item description..."
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label>Category</Label>
                <Input 
                  value={itemForm.category}
                  onChange={(e) => setItemForm({...itemForm, category: e.target.value})}
                  placeholder="Food Supplies"
                />
              </div>
              <div className="grid gap-2">
                <Label>Unit</Label>
                <Select value={itemForm.unit} onValueChange={(v) => setItemForm({...itemForm, unit: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nos">Numbers</SelectItem>
                    <SelectItem value="kg">Kilograms</SelectItem>
                    <SelectItem value="g">Grams</SelectItem>
                    <SelectItem value="l">Liters</SelectItem>
                    <SelectItem value="ml">Milliliters</SelectItem>
                    <SelectItem value="pcs">Pieces</SelectItem>
                    <SelectItem value="box">Box</SelectItem>
                    <SelectItem value="pack">Pack</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Tax Rate %</Label>
                <Select value={String(itemForm.tax_rate)} onValueChange={(v) => setItemForm({...itemForm, tax_rate: Number(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="12">12%</SelectItem>
                    <SelectItem value="18">18%</SelectItem>
                    <SelectItem value="28">28%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="grid gap-2">
                <Label>Min Stock</Label>
                <Input 
                  type="number"
                  value={itemForm.min_stock_level}
                  onChange={(e) => setItemForm({...itemForm, min_stock_level: Number(e.target.value)})}
                />
              </div>
              <div className="grid gap-2">
                <Label>Max Stock</Label>
                <Input 
                  type="number"
                  value={itemForm.max_stock_level}
                  onChange={(e) => setItemForm({...itemForm, max_stock_level: Number(e.target.value)})}
                />
              </div>
              <div className="grid gap-2">
                <Label>Reorder Point</Label>
                <Input 
                  type="number"
                  value={itemForm.reorder_point}
                  onChange={(e) => setItemForm({...itemForm, reorder_point: Number(e.target.value)})}
                />
              </div>
              <div className="grid gap-2">
                <Label>Reorder Qty</Label>
                <Input 
                  type="number"
                  value={itemForm.reorder_quantity}
                  onChange={(e) => setItemForm({...itemForm, reorder_quantity: Number(e.target.value)})}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Standard Cost (₹)</Label>
              <Input 
                type="number"
                step="0.01"
                value={itemForm.standard_cost}
                onChange={(e) => setItemForm({...itemForm, standard_cost: Number(e.target.value)})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveItem} className="bg-orange-500 hover:bg-orange-600">
              {selectedItem ? 'Update' : 'Create'} Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
